// Зачёт-вызов: три задачи, которыми ученик предъявляет знание темы.
//
// Правило 2 `docs/11-entry.md`. Отдельный экран, а не режим «Решения»: там
// задача из набора дня, здесь испытание с исходом, и общего у них только поле
// ввода. У Diagnostic такая же своя копия по той же причине.
//
// **Сдаётся целиком, с 14 сентября 2026.** Все три задачи на экране, ходить
// между ними можно свободно, вердиктов до сдачи нет ни одного. Причина не в
// строгости: три задачи идут по ОДНОМУ навыку и верхней ступени, и разбор
// после первой учит методу для второй и третьей — зачёт тогда мерит не
// «знает», а «быстро схватывает с разбора», а этим числом система кредитует до
// пятидесяти навыков разом. Разбор никуда не делся, он приходит после сдачи,
// когда портить измерение уже нечем.
//
// Тикающего счётчика на экране нет, хотя время зачёта считается и после сдачи
// показывается. Решение записано в src/lib/clock.ts: времена, снятые ПОД
// счётчиком, меряют сами себя, и медианы по шаблонам станут непригодны.
//
// «Не знаю» здесь нет намеренно. В диагностике это самый сильный сигнал и
// экономия вопросов; в зачёте — способ провалить его быстрее, а зачёт и так
// ничего не отнимает. Кто не знает, просто не начинает.

import { useEffect, useRef, useState } from "react";
import { api, useMutation, useQuery, type TestOutSubmitResult } from "../api";
import { newClock, type Clock } from "../lib/clock";
import { Formula } from "../lib/Formula";
import { plural } from "../lib/dates";
import { reasonOf } from "../lib/errors";
import { Scratch } from "../ui/Scratch";
import { Solution } from "../ui/Solution";

/** «6 минут 20 секунд» — время зачёта, показанное один раз, после сдачи. */
function spent(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const min = Math.floor(total / 60);
  const sec = total % 60;
  if (min === 0) return `${sec} ${plural(sec, "секунда", "секунды", "секунд")}`;
  if (sec === 0) return `${min} ${plural(min, "минута", "минуты", "минут")}`;
  return `${min} ${plural(min, "минута", "минуты", "минут")} ${sec} ${plural(sec, "секунда", "секунды", "секунд")}`;
}

export function TestOut({
  learnerKey,
  onDone,
  returnLabel = "К набору дня",
}: {
  learnerKey: string;
  onDone: () => void;
  returnLabel?: string;
}) {
  const state = useQuery(api.testout.current, { learnerKey });
  const saveDraft = useMutation(api.testout.draft);
  const submit = useMutation(api.testout.submit);
  const abandon = useMutation(api.testout.abandon);

  const [at, setAt] = useState(0);
  const [raw, setRaw] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [unreadable, setUnreadable] = useState<number[]>([]);
  const [result, setResult] = useState<TestOutSubmitResult | null>(null);
  const [leaving, setLeaving] = useState(false);

  // Часы на задачу. Перезапускаются при переходе, а накопленное уходит в
  // черновик — так задача, к которой вернулись, считает оба захода.
  const clock = useRef<Clock>(newClock());
  const seeded = useRef(false);

  // Черновики с сервера подставляются ОДИН раз, на входе. Дальше поле ведёт
  // себя как обычное поле ввода: подписка перезапускается на каждом
  // сохранении, и подставлять из неё значило бы затирать набранное.
  useEffect(() => {
    if (seeded.current || state === undefined || state === null) return;
    seeded.current = true;
    setRaw(state.drafts);
  }, [state]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") clock.current.pause();
      else clock.current.resume();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  if (state === undefined) {
    return (
      <div className="stack">
        <div className="skeleton" style={{ width: 180 }} />
        <div className="card">
          <div className="skeleton" />
        </div>
      </div>
    );
  }

  /** Сохранить черновик текущей задачи и перезапустить часы. */
  const flush = (index: number, next: string[]): void => {
    const key = state?.questions[index]?.itemKey;
    if (key === undefined) return;
    const ms = clock.current.elapsed();
    clock.current = newClock();
    void saveDraft({ learnerKey, itemKey: key, raw: next[index] ?? "", ms }).catch(
      (e: unknown) => console.error(e),
    );
  };

  const goto = (next: number): void => {
    if (state === null || next === at) return;
    flush(at, raw);
    setUnreadable([]);
    setAt(next);
  };

  const send = (): void => {
    if (state === null || busy) return;
    flush(at, raw);
    setBusy(true);
    setFailed(null);
    void submit({ learnerKey })
      .then((r) => {
        setBusy(false);
        if (r.status === "unreadable") {
          setUnreadable(r.unreadable);
          setAt(r.unreadable[0] ?? at);
          return;
        }
        setResult(r);
      })
      .catch((e: unknown) => {
        setBusy(false);
        setFailed(reasonOf(e));
      });
  };

  // ─── Итог ────────────────────────────────────────────────────────────────
  // Своё состояние, а не `state`: сданный зачёт исчезает из подписки, и без
  // этого панель итога промелькнула бы вместе с документом. Тот же приём, что
  // в диагностике.
  if (result !== null || state === null) {
    const passed = result !== null && result.correct >= 3;
    const two = result !== null && result.correct === 2;
    return (
      <div className="cols cols--solo">
        <div className="col">
          <div className="eyebrow">Зачёт</div>
          <h1 className="h1">
            {result === null ? "Зачёта нет" : passed ? "Зачтено" : "Не зачтено"}
          </h1>
          {result === null ? (
            <p className="lede">Этот зачёт уже закрыт.</p>
          ) : (
            <>
              <p className="lede">
                <span className="num">{result.correct}</span> из{" "}
                <span className="num">{result.results.length}</span> ·{" "}
                {spent(result.ms)}
              </p>
              <p className="lede" style={{ marginTop: 4 }}>
                {passed ? (
                  <>
                    Засчитано <span className="num">{result.credited}</span>{" "}
                    {plural(result.credited, "навык", "навыка", "навыков")} — тема и
                    всё, на чём она стоит.
                  </>
                ) : two ? (
                  "Две из трёх: тема открыта, но не зачтена. Она встанет в набор дня, и предпосылки останутся как были."
                ) : (
                  "Ничего не изменилось — ни владения, ни открытых тем. Провал зачёта не отнимает ничего, пересдать можно на других задачах."
                )}
              </p>

              {/*
                Разборы всех трёх задач подряд, здесь и только здесь. До сдачи
                их не было намеренно; после неё прятать нечего, а самая ценная
                секунда системы — та, что после ошибки.
              */}
              <div className="stack" style={{ marginTop: 20 }}>
                {result.results.map((r, i) => {
                  return (
                    <div className="card" key={r.itemKey}>
                      <div className="card__label">
                        Задача {i + 1} ·{" "}
                        {r.verdict === "correct"
                          ? "верно"
                          : r.verdict === "blank"
                            ? "без ответа"
                            : "неверно"}
                      </div>
                      {/* Условие приходит вместе с итогом: подписки на
                          сданный зачёт уже нет, и до 16 сентября 2026 разборы
                          стояли здесь без задач, к которым написаны. */}
                      <div className="statement" style={{ marginTop: 10 }}>
                        <Formula source={r.statement} chip />
                      </div>
                      <div
                        className={`banner ${r.verdict === "correct" ? "banner--grow" : "banner--miss"}`}
                        style={{ marginTop: 12 }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div className="banner__line">
                            {r.verdict === "blank" ? (
                              "Ответа не было"
                            ) : (
                              <>
                                Твой ответ <Formula source={r.raw} />
                              </>
                            )}
                          </div>
                          {/*
                            Эталон показывается только там, где ответ не
                            сошёлся. На верном «Верно 8» под «Твой ответ 8» —
                            то же число дважды, и читается это как сбой, а не
                            как подтверждение.
                          */}
                          {r.answerDisp !== null && r.verdict !== "correct" ? (
                            <div className="banner__line">
                              Верно{" "}
                              <span className="underline--grow">
                                <Formula source={r.answerDisp} />
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {/* Разбор идёт по всем трём задачам, и по верно
                          решённым тоже. Измерение он уже не портит: зачёт
                          сдан, оценка посчитана. */}
                      <Solution steps={r.solution} />
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <div className="row row--actions" style={{ marginTop: 20 }}>
            <button className="btn btn--primary btn--big" onClick={onDone}>
              {returnLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Зачёт идёт ──────────────────────────────────────────────────────────
  const question = state.questions[at];
  const filled = raw.filter((r) => r.trim().length > 0).length;
  const blanks = state.total - filled;

  return (
    <div className="cols">
      <div className="col">
        <div>
          <div className="eyebrow">Зачёт</div>
          <h1 className="h1">
            Задача <span className="num">{at + 1}</span> из{" "}
            <span className="num">{state.total}</span>
          </h1>
          {/*
            Здесь стояло «верных пока 1». Убрано: счёт по ходу — это обратная
            связь, а она портит измерение (см. шапку файла). Осталось то, что
            обратной связью не является, — сколько задач заполнено.
          */}
          <p className="lede">
            Ответов вписано <span className="num">{filled}</span> из{" "}
            <span className="num">{state.total}</span>. Проверка одна, после
            сдачи; провал не отнимает ничего.
          </p>
        </div>

        {/* Переходы между задачами. До сдачи они бесплатны: показывать по
            ним нечего, а порядок решения выбирает ученик. */}
        <div className="row" style={{ gap: 8, marginTop: 4 }}>
          {state.questions.map((q, i) => (
            <button
              key={q.itemKey}
              className={`btn btn--quiet${i === at ? " btn--on" : ""}`}
              style={{ padding: "8px 16px", fontSize: 14 }}
              onClick={() => goto(i)}
            >
              {i + 1}
              {raw[i]?.trim() ? " ·" : ""}
            </button>
          ))}
        </div>

        <div className="card">
          <div className="card__label">Условие</div>
          {question ? (
            <div className="statement">
              <Formula source={question.statement} chip />
            </div>
          ) : (
            <div className="skeleton" style={{ marginTop: 14, height: 26 }} />
          )}
        </div>

        <div className="card">
          <div className="answer">
            <span className="answer__label">Ответ</span>
            <input
              className={`field${unreadable.includes(at) ? " field--bad" : ""}`}
              value={raw[at] ?? ""}
              autoFocus
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              placeholder={question?.inputHint ?? ""}
              onChange={(event) => {
                const next = [...raw];
                next[at] = event.target.value;
                setRaw(next);
                if (unreadable.includes(at)) setUnreadable([]);
              }}
              onBlur={() => flush(at, raw)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                // Enter ведёт к следующей незаполненной задаче, а не сдаёт
                // зачёт: сдача необратима, и вешать её на клавишу, которой
                // заканчивают строку, значит сдавать по ошибке.
                const next = state.questions.findIndex(
                  (_, i) => i !== at && !(raw[i] ?? "").trim(),
                );
                if (next >= 0) goto(next);
                else flush(at, raw);
              }}
            />
          </div>

          {unreadable.includes(at) ? (
            <div className="notice" style={{ marginTop: 12 }}>
              Не разобрано. Поле принимает {question?.inputHint}. Зачёт не сдан и
              ничего не потерял — поправь ответ и сдай снова.
            </div>
          ) : null}
        </div>

        <div className="row row--actions" style={{ marginTop: 16 }}>
          <button className="btn btn--primary btn--big" disabled={busy} onClick={send}>
            {busy ? "Проверяю" : "Сдать зачёт"}
          </button>
          {/*
            Выход. До 14 сентября 2026 его не было вовсе, и незакрытый зачёт
            запирал ученика на экране: приложение уводит сюда, пока документ
            открыт. Подтверждение одно и короткое — выход ничего не отнимает,
            но и вернуться в тот же зачёт уже нельзя.
          */}
          {leaving ? (
            <div className="muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
              Выйти? Зачёт закроется несданным; ничего не отнимется, но эти три
              задачи не вернутся.{" "}
              <button
                className="link"
                onClick={() => {
                  void abandon({ learnerKey })
                    .then(onDone)
                    .catch((e: unknown) => setFailed(reasonOf(e)));
                }}
              >
                Выйти
              </button>{" "}
              ·{" "}
              <button className="link" onClick={() => setLeaving(false)}>
                Остаться
              </button>
            </div>
          ) : (
            <button className="btn btn--big" onClick={() => setLeaving(true)}>
              Выйти из зачёта
            </button>
          )}
        </div>

        {blanks > 0 ? (
          <div className="muted" style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
            Без ответа {blanks === 1 ? "осталась" : "остались"}{" "}
            <span className="num">{blanks}</span>{" "}
            {plural(blanks, "задача", "задачи", "задач")} — сдать можно и так, они
            пойдут как неверные.
          </div>
        ) : null}

        {failed === null ? null : (
          <div className="notice" style={{ marginTop: 12 }}>
            {failed}
          </div>
        )}
      </div>

      <div className="col">
        {/*
          Черновик на весь зачёт, а не по задаче — в отличие от «Решения»,
          поэтому ключа у панели здесь нет. Между тремя задачами ходят
          свободно, и чистый лист на каждом переходе стирал бы работу ровно
          тогда, когда ученик пользуется тем, что мы только что разрешили.

          Места на три задачи хватает: лист тетрадки 1500×1000 точек, столбик
          48×20 клеток, доска бесконечна. Свёрнутое окно стоит на середине
          листа, к свободному месту уходят прокруткой.
        */}
        {question ? <Scratch statement={question.statement} /> : null}

        <div className="card card--dashed">
          <div className="card__label">Зачем это</div>
          <div className="muted" style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
            Объявить «я это знаю» нельзя — система считает владение из решённых
            задач, а не из сказанного. Зато предъявить дёшево: карта знает, что
            из чего следует, и одна сданная вершина закрывает десятки тем под
            ней разом.
          </div>
        </div>

        <div className="card card--dashed">
          <div className="card__label">Как это устроено</div>
          <div className="muted" style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
            Три задачи по одной теме, верхняя ступень, без подсказок. Порядок
            любой, ответы можно переписывать. Проверка одна — после «Сдать»; тогда
            же придут разборы всех трёх.
          </div>
        </div>
      </div>
    </div>
  );
}
